import { CustomRange } from "@components/CustomRange";
import { AddTipProps } from "@components/donate/DonateV3/PaymentProcess/components/AddTip/types";
import { DonateModalAction } from "@components/donate/DonateV3/types";
import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import { colorPalette } from "@every.org/common/src/display/palette";
import { TIP_SLIDER_DEFAULT_SETTINGS } from "@every.org/common/src/entity/constants";
import { CustomEvent } from "@every.org/common/src/helpers/analytics";

import { useUserExperiment } from "src/utility/abtesting";
import { trackEvent } from "src/utility/analytics";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";

export function getInitialSliderValues(percentage?: number) {
  const { min, max, step, initial } = TIP_SLIDER_DEFAULT_SETTINGS;

  const validPercentage =
    percentage !== undefined &&
    percentage >= min &&
    percentage <= max &&
    !(percentage % step)
      ? percentage
      : undefined;

  if (percentage === undefined) {
    return {
      initialSliderValue: initial,
      initialShowSlider: true,
    };
  }

  if (typeof percentage === "number" && typeof validPercentage === "number") {
    return {
      initialSliderValue: percentage,
      initialShowSlider: true,
    };
  }

  return {
    initialSliderValue: initial,
    initialShowSlider: false,
  };
}

function getSliderAdjust({
  tipPercentage,
  sliderValue,
}: {
  tipPercentage: number;
  sliderValue: number;
}): CustomEvent | null {
  if (sliderValue === 0) {
    return CustomEvent.TIP_ZERO;
  }
  if (sliderValue > tipPercentage) {
    return CustomEvent.TIP_UP;
  }
  if (sliderValue < tipPercentage) {
    return CustomEvent.TIP_DOWN;
  }
  // No event to track if they match
  return null;
}

export const DefaultTipSlider = (props: AddTipProps) => {
  const { formContext, form, onChange, isZeroTip, amountBig } = props;
  const currency = form.watch("currency");

  const sliderTest = useUserExperiment(
    "tip_slider_step_05_2",
    formContext.donateAction === DonateModalAction.UPDATE
  );

  const sliderStep = sliderTest.get<number>(
    "step",
    TIP_SLIDER_DEFAULT_SETTINGS.step
  );
  const tipPercentage = sliderTest.get<number>(
    "def",
    TIP_SLIDER_DEFAULT_SETTINGS.initial
  );

  const sliderSettings = useMemo(() => {
    const { initialSliderValue } = getInitialSliderValues(
      formContext.donationTipPercent
    );
    return {
      ...TIP_SLIDER_DEFAULT_SETTINGS,
      initial: initialSliderValue,
      step: sliderStep,
    };
  }, [formContext.donationTipPercent, sliderStep]);

  const [sliderValue, setSliderValue] = useState(sliderSettings.initial);
  const [debouncedSliderValue] = useDebounce(sliderValue, 1500);

  useEffect(() => {
    form.setValue("suggestedTipPercent", tipPercentage);
  }, [tipPercentage, form]);

  useEffect(() => {
    form.setValue("actualTipPercent", sliderValue);
  }, [sliderValue, form]);

  // Track tip adjustments based on debouncedSliderValue
  useEffect(() => {
    const event = getSliderAdjust({
      tipPercentage,
      sliderValue: debouncedSliderValue,
    });
    if (event) {
      trackEvent(event, {
        suggestedTipPercent: tipPercentage,
        actualTipPercent: debouncedSliderValue,
      });
    }
  }, [debouncedSliderValue, tipPercentage]);

  const computedValue = useMemo(
    () => amountBig.div(100).mul(sliderValue),
    [amountBig, sliderValue]
  );

  useEffect(() => {
    onChange(computedValue.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedValue]);

  return (
    <CustomRange
      themeColor={isZeroTip ? colorPalette.grayMedium : formContext.themeColor}
      {...sliderSettings}
      value={sliderValue}
      onChange={setSliderValue}
      popoverContent={displayCurrencyValueInUserLocale({
        currencyValue: {
          currency,
          amount: computedValue,
        },
      })}
    />
  );
};
